import {
  LocalFilterDto,
  RiskValueRequestTypeDto
} from '_/src/app/services/api/generatedApi'
import {
  IQRAGraphCsvExportParams,
  IQRARiskByCategoryGraphApiResult,
  IQRARiskBySystemGraphApiResult
} from '_/src/app/services/api/qra/graphs'
import { IQRANavUtilsConsumerComponentInitProps } from 'common/utils/navigation/qraNavUtils'

import { RiskForecastGraphs } from './Implementations/RiskForecastGraphs'

/**
 * Match string with mock `graphType`
 */
export enum QRAGraphType {
  THREATS = 'riskbythreat',
  CONSEQUENCES = 'riskbyconsequence',
  RISK_BY_LINE = 'riskbyline',
  LINEAR_REFERENCE = 'riskbylinearreference',
  RISK_FORECAST = 'riskforecast',
  RISK_BY_SIGNIFICANCE_LEVEL = 'riskbysignificancelevel',
  RISK_LEVEL_BY_OUTCOME_SCENARIO = 'riskbyoutcomescenario',
  RISK_BY_RELEASE_TYPE = 'riskbyreleasetype',
  RISK_BY_SYSTEM = 'riskbysystem',
  RISK_BY_CONSEQUENCE_AREAS = 'riskbyconsequenceareas'
}

export enum CommonChartStateName {
  BASELINE = 'baseline',
  WIS = 'wis',
  TIME_DEPENDENT = 'time_dependent',
  RESIDENT = 'resident',
  TIME_INDEPENDENT = 'time_independent'
}

// For use by component that that generates the required data for `IGraphsViewProps`
export interface IGraphsViewBaseProps
  extends IQRANavUtilsConsumerComponentInitProps {}

export interface ISystemGraphsViewProps extends IGraphsViewBaseProps {
  showLoading: boolean
  charts?: IQRAChartObjects
  riskByPipelineLengthGraphData?: IQRARiskByCategoryGraphApiResult
  riskBySystemGraphData?: IQRARiskBySystemGraphApiResult
  hasLineViewNavigation: boolean
  itemsFromGraphType?: Record<string, () => QRAChartItems[] | undefined>
  graphLoadingByType?: IGraphLoadingByType
  csvExportParams?: IQRAGraphCsvExportParams
}

export interface ILineGraphsViewProps extends IGraphsViewBaseProps {
  showLoading: boolean
  charts?: IQRAChartObjects
  riskByPipelineLengthGraphData?: IQRARiskByCategoryGraphApiResult
  riskBySystemGraphData?: IQRARiskBySystemGraphApiResult
  graphLoadingByType?: IGraphLoadingByType
  csvExportParams?: IQRAGraphCsvExportParams
}

export type IGraphLoadingByType = Partial<Record<QRAGraphType, boolean>>

export interface IQRAGraphQueryParams {
  systemScopeId?: string
  baselineId1?: string
  baselineId2?: string
  assessmentId?: string
  continuousRouteId?: string
}

export interface IQRAGraphBools {
  [QRAGraphType.THREATS]?: boolean
  [QRAGraphType.CONSEQUENCES]?: boolean
  [QRAGraphType.RISK_BY_LINE]?: boolean
  [QRAGraphType.RISK_FORECAST]?: boolean
  [QRAGraphType.LINEAR_REFERENCE]?: boolean
  [QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL]?: boolean
  [QRAGraphType.RISK_LEVEL_BY_OUTCOME_SCENARIO]?: boolean
  [QRAGraphType.RISK_BY_RELEASE_TYPE]?: boolean
  [QRAGraphType.RISK_BY_SYSTEM]?: boolean
  [QRAGraphType.RISK_BY_CONSEQUENCE_AREAS]?: boolean
}

export interface IChartData {
  categoryIds: number[]
  categories: string[]
  values: IChartDataValues
  labels: IChartSeriesLabels
  threatCategoryTooltipGroups?: IThreatCategoryTooltipGroup[]
}

export interface IChartDataValues {
  // May contain `CommonChartStateName`s
  [stateName: string]: number[]
}

export interface IChartSeriesLabels {
  [label: string]: string
}

export interface IThreatCategoryTooltipSeriesValues {
  [stateName: string]: number
}

export interface IThreatCategoryTooltipMember {
  key: string
  label: string
  sortOrder: number
  values: IThreatCategoryTooltipSeriesValues
}

export interface IThreatCategoryTooltipGroup {
  key: string
  label: string
  sortOrder: number
  members: IThreatCategoryTooltipMember[]
  totals: IThreatCategoryTooltipSeriesValues
}

export interface IQRAThreatChartValue {
  key: string
  value: number
}

export interface IQRAThreatChartSourceCategory {
  key: string
  label: string
  sortOrder: number
  values: IQRAThreatChartValue[]
}

export interface IQRAThreatChartGrouping {
  key: string
  label: string
  members: string[]
  sortOrder: number
}

export interface IQRAThreatChartGroupingConfig {
  label: string
  key: string
  groupings: IQRAThreatChartGrouping[]
  sortOrder: number
}

export interface IConsequenceCategoryTooltipSeriesValues {
  [stateName: string]: number
}

export interface IConsequenceCategoryTooltipMember {
  key: string
  label: string
  sortOrder: number
  values: IConsequenceCategoryTooltipSeriesValues
}

export interface IQRAConsequenceCategoryTooltipGroup {
  key: string
  label: string
  sortOrder: number
  members: IConsequenceCategoryTooltipMember[]
  totals: IConsequenceCategoryTooltipSeriesValues
}

export interface IQRAConsequenceChartValue {
  key: string
  value: number
}

export interface IQRAConsequenceChartSourceCategory {
  key: string
  label: string
  sortOrder: number
  values: IQRAConsequenceChartValue[]
}

export interface IQRAConsequenceChartGrouping {
  key: string
  label: string
  members: string[]
  sortOrder: number
}

export interface IQRAConsequenceChartGroupingConfig {
  label: string
  key: string
  groupings: IQRAConsequenceChartGrouping[]
  sortOrder: number
}

// REVIEW: Promote this into main @type?
// Based on structure from API
export interface IQRAChartObjects {
  [chart: string]: IQRAChartBaseObject | undefined
}

export interface IQRAChartBaseObject {
  graphType: QRAGraphType
}

export type QRAChartItems =
  | QRARiskForecastChartItems
  | QRAStandardChartItems
  | QRALinearReferenceChartItems

type QRAStandardChartItems = {
  id: number
  name: string
  values: Array<{
    key: string
    value: number
  }>
}

export interface IQRAStandardChartObject extends IQRAChartBaseObject {
  items: Array<QRAStandardChartItems>
}

export interface IQRAThreatChartObject extends IQRAStandardChartObject {
  groupingConfig?: IQRAThreatChartGroupingConfig[]
  sourceCategories?: IQRAThreatChartSourceCategory[]
}

export interface IQRAConsequenceChartObject extends IQRAStandardChartObject {
  groupingConfig?: IQRAConsequenceChartGroupingConfig[]
  sourceCategories?: IQRAConsequenceChartSourceCategory[]
}

export interface IQRARiskByRoute {
  id: number
  name: string
  routeTotalRiskBaseline: number
  routeTotalRiskScenario?: number
  pipeLength?: number
  categoriesResult: IQRAThreatChartSourceCategory[]
}

export interface IQRARiskByRouteChartObject extends IQRAChartBaseObject {
  routes: IQRARiskByRoute[]
  groupingConfig: IQRAThreatChartGroupingConfig[]
}

export type QRARiskForecastChartItems = {
  year: number
  items: Array<{
    name: string
    values: Array<{
      key: string
      value: number
    }>
    members?: IQRAThreatChartSourceCategory[]
  }>
}

export interface IQRARiskForecastChartObject extends IQRAChartBaseObject {
  items: Array<QRARiskForecastChartItems>
}

type QRALinearReferenceChartItems = {
  id: number
  values: [
    {
      key: string
      value: {
        fromMeasure: number
        toMeasure: number
        risk: number
      }
    }
  ]
}

export interface IQRALinearReferenceChartObject extends IQRAChartBaseObject {
  acceptanceCategories: [
    {
      id: number
      minValue: number
      maxValue: number
      name: string
      colour: string
      code: string
    }
  ]
  items: QRALinearReferenceChartItems[]
}

export interface IQRAGraphExportQueryParams {
  systemScopeId: number
  continuousRouteId?: number | null
  assessmentId?: number | null
  threatConfigurationKey: string
  sortOptions: ChartSortOptionsDto
  groupingOptions: ChartGroupingOptionsDto
  graphRequestParams: IQRAGraphRequestParamsExport
  savedStateId?: number | null
  baselineAcceptanceCategory?: number[]
  baselineSystemIds?: number[]
  scenarioSystemIds?: number[]
  wisAcceptanceCategory?: number[]
  forecastChartType?: RiskForecastGraphs
  enabledThreatLabels?: string[]
}

export interface IQRAGraphRequestParamsExport {
  graphSelection: IQRAGraphBools
  riskValue: RiskValueRequestTypeDto
  localFilter: LocalFilterDto
}

export enum ChartSortOptionsDto {
  Default = 1,
  RiskAscending = 2, // low to high
  RiskDescending = 3, // high to low
  AlphabeticalAscending = 4, // A to Z
  AlphabeticalDescending = 5 // Z to A
}

export enum ChartGroupingOptionsDto {
  None = 1,
  ThreatCategory = 2,
  Threat = 3
}
